import React, { useState, useCallback, useEffect } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import {
  Search, RefreshCw, TrendingUp, TrendingDown, Shield, Zap,
  DollarSign, BarChart3, ChevronDown, ChevronUp, Target,
  AlertTriangle, ArrowRight, Loader2, Trash2, Sparkles, Brain,
  Activity, Lock, Crosshair, Layers, ArrowUpDown, Bookmark
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AskApeInlinePanel } from './AskApeExpert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  income: { label: 'Income', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: DollarSign },
  bullish: { label: 'Bullish', color: 'bg-green-100 text-green-800 border-green-300', icon: TrendingUp },
  bearish: { label: 'Bearish', color: 'bg-red-100 text-red-800 border-red-300', icon: TrendingDown },
  neutral: { label: 'Neutral', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Activity },
  volatility: { label: 'Volatility', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: Zap },
  hedging: { label: 'Hedging', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Shield },
  directional: { label: 'Directional', color: 'bg-cyan-100 text-cyan-800 border-cyan-300', icon: Crosshair },
};

const RISK_COLOR = {
  'Low': 'text-green-600',
  'Low-Medium': 'text-lime-600',
  'Medium': 'text-amber-600',
  'High': 'text-red-600',
};

const WATCHLIST_KEY = 'ofs_strategy_watchlist';

const getWatchlist = () => {
  try {
    const stored = localStorage.getItem(WATCHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const StrategyScanner = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { model: aiModel, setModel: setAiModel } = useAIModel();
  const [expandedCards, setExpandedCards] = useState({});
  const [loadingMore, setLoadingMore] = useState({});
  const [moreTrades, setMoreTrades] = useState({});
  const [filterCategory, setFilterCategory] = useState('all');
  const [watchlistIds, setWatchlistIds] = useState(getWatchlist);

  // Sync watchlist from other tabs
  useEffect(() => {
    const handler = () => setWatchlistIds(getWatchlist());
    window.addEventListener('watchlist-updated', handler);
    return () => window.removeEventListener('watchlist-updated', handler);
  }, []);

  const toggleWatchlist = (stratId) => {
    const current = getWatchlist();
    let updated;
    if (current.includes(stratId)) {
      updated = current.filter(id => id !== stratId);
      toast.success('Removed from watchlist');
    } else {
      updated = [...current, stratId];
      toast.success('Added to watchlist');
    }
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(updated));
    setWatchlistIds(updated);
    window.dispatchEvent(new Event('watchlist-updated'));
  };

  const runScan = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/strategy-scanner/scan`, {
        ai_model: aiModel,
        force_refresh: forceRefresh,
      }, { timeout: 30000 });

      const resData = res.data;

      // If cached result returned directly
      if (resData.strategies) {
        setData(resData);
        toast.success(resData.cached ? 'Loaded cached results' : `Scan complete!`);
        setLoading(false);
        return;
      }

      // Background job started - poll for results
      if (resData.job_id) {
        toast.info('Scan started. Analyzing 20 strategies...');
        const jobId = resData.job_id;
        let attempts = 0;
        const maxAttempts = 60; // 2 minutes max

        const poll = async () => {
          attempts++;
          try {
            const statusRes = await axios.get(`${API}/strategy-scanner/status/${jobId}`, { timeout: 10000 });
            if (statusRes.data.strategies) {
              // Complete
              setData(statusRes.data);
              toast.success(`Scan complete! ${statusRes.data.strategy_count} strategies analyzed`);
              setLoading(false);
              return;
            }
            // Still running
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              toast.error('Scan timed out. Please try again.');
              setLoading(false);
            }
          } catch (err) {
            if (err.response?.status === 500) {
              toast.error('Scan failed: ' + (err.response?.data?.detail || 'Unknown error'));
              setLoading(false);
            } else if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              toast.error('Scan timed out.');
              setLoading(false);
            }
          }
        };
        setTimeout(poll, 3000); // First poll after 3s
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Failed to start scan. Please try again.');
      setLoading(false);
    }
  }, [aiModel]);

  const handleForceRefresh = async () => {
    try {
      await axios.delete(`${API}/strategy-scanner/clear-cache`);
      toast.info('Cache cleared. Generating fresh scan...');
    } catch {
      // ignore
    }
    runScan(true);
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const loadMoreTrades = async (strategyId) => {
    setLoadingMore(prev => ({ ...prev, [strategyId]: true }));
    try {
      const res = await axios.post(`${API}/strategy-scanner/more`, {
        strategy_id: strategyId,
        ai_model: aiModel,
      }, { timeout: 120000 });
      setMoreTrades(prev => ({ ...prev, [strategyId]: res.data.additional_trades }));
      toast.success('Additional trades loaded!');
    } catch (err) {
      console.error('Load more error:', err);
      toast.error('Failed to load more recommendations');
    } finally {
      setLoadingMore(prev => ({ ...prev, [strategyId]: false }));
    }
  };

  const filteredStrategies = data?.strategies?.filter(
    s => filterCategory === 'all' || s.category === filterCategory
  ) || [];

  const categoryCounts = {};
  (data?.strategies || []).forEach(s => {
    categoryCounts[s.category] = (categoryCounts[s.category] || 0) + 1;
  });

  return (
    <div className="space-y-6" data-testid="strategy-scanner">
      {/* Header Card */}
      <Card className="glass-effect border-0 shadow-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Target className="h-6 w-6 text-amber-400" />
                </div>
                Strategy Scanner
              </CardTitle>
              <p className="text-slate-400 mt-1 text-sm">
                20 options strategies with AI-powered actionable trade picks
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <ModelSelector />

              {!data && !loading && (
                <Button
                  onClick={() => runScan(false)}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold"
                  data-testid="run-scan-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Run Scan
                </Button>
              )}

              {data && (
                <>
                  <Button
                    onClick={() => runScan(false)}
                    disabled={loading}
                    variant="outline"
                    size="icon"
                    className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                    data-testid="refresh-scan-btn"
                    title="Refresh"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    onClick={handleForceRefresh}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    className="border-amber-500/40 text-amber-400 hover:bg-amber-500/20"
                    data-testid="force-refresh-scan-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    New Scan
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Loading State */}
      {loading && !data && (
        <Card className="glass-effect border-0 shadow-lg">
          <CardContent className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-amber-500 mb-4" />
            <p className="text-lg font-medium text-slate-700">Scanning 20 strategies across 40 stocks...</p>
            <p className="text-sm text-slate-500 mt-1">AI is analyzing market data and generating trade picks (30-60s)</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {data && (
        <>
          {/* AI Error Banner */}
          {data.ai_error && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3 flex items-center gap-3 text-sm">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <span className="font-medium text-amber-800">AI trade suggestions unavailable.</span>
                  <span className="text-amber-700 ml-1">
                    {data.ai_error.includes('Budget') 
                      ? 'LLM key budget exceeded. Go to Profile > Universal Key > Add Balance to enable AI picks.'
                      : 'AI service temporarily unavailable. Strategy definitions are still shown below. Try "New Scan" later.'}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filter Bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500 font-medium mr-1">Filter:</span>
            <Badge
              variant={filterCategory === 'all' ? 'default' : 'outline'}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setFilterCategory('all')}
              data-testid="filter-all"
            >
              All ({data.strategies?.length || 0})
            </Badge>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              const count = categoryCounts[key] || 0;
              if (count === 0) return null;
              const Icon = cfg.icon;
              return (
                <Badge
                  key={key}
                  variant={filterCategory === key ? 'default' : 'outline'}
                  className={`cursor-pointer hover:opacity-80 transition-opacity ${filterCategory === key ? '' : cfg.color}`}
                  onClick={() => setFilterCategory(key)}
                  data-testid={`filter-${key}`}
                >
                  <Icon className="h-3 w-3 mr-1" />
                  {cfg.label} ({count})
                </Badge>
              );
            })}
            {data.cached && (
              <Badge variant="secondary" className="ml-auto text-xs">
                Cached
              </Badge>
            )}
          </div>

          {/* Strategy Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStrategies.map((strat, idx) => {
              const catCfg = CATEGORY_CONFIG[strat.category] || CATEGORY_CONFIG.neutral;
              const CatIcon = catCfg.icon;
              const isExpanded = expandedCards[strat.id];
              const extraTrades = moreTrades[strat.id];
              const isLoadingMore = loadingMore[strat.id];
              const isWatched = watchlistIds.includes(strat.id);

              return (
                <Card
                  key={strat.id}
                  className={`border hover:shadow-md transition-all duration-200 ${isWatched ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`strategy-card-${strat.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-base font-bold text-slate-900">{strat.name}</span>
                          <Badge className={`text-xs border ${catCfg.color}`} variant="outline">
                            <CatIcon className="h-3 w-3 mr-1" />
                            {catCfg.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{strat.description}</p>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 ${isWatched ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-500'}`}
                          onClick={() => toggleWatchlist(strat.id)}
                          data-testid={`bookmark-${strat.id}`}
                          title={isWatched ? 'Remove from watchlist' : 'Add to watchlist'}
                        >
                          <Bookmark className={`h-4 w-4 ${isWatched ? 'fill-current' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpand(strat.id)}
                          data-testid={`expand-${strat.id}`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className={`font-semibold ${RISK_COLOR[strat.risk_level] || 'text-slate-600'}`}>
                        <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                        {strat.risk_level} Risk
                      </span>
                      <span className="text-slate-500">|</span>
                      <span className="text-indigo-600 font-semibold">
                        <BarChart3 className="h-3 w-3 inline mr-0.5" />
                        {strat.typical_win_rate}% Win Rate
                      </span>
                      <span className="text-slate-500">|</span>
                      <span className="text-emerald-600">
                        {strat.avg_return}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {/* Trade Picks */}
                    {strat.trades && strat.trades.length > 0 ? (
                      <div className="space-y-2 mt-2">
                        {strat.trades.map((trade, tIdx) => (
                          <div
                            key={tIdx}
                            className="bg-gradient-to-r from-slate-50 to-indigo-50/30 rounded-lg p-3 border border-slate-100"
                            data-testid={`trade-${strat.id}-${tIdx}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs font-bold bg-white">
                                {trade.symbol}
                              </Badge>
                              <span className="text-xs font-medium text-slate-400">Trade {tIdx + 1}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-800">{trade.action}</p>
                            <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-600 flex-wrap">
                              <span><DollarSign className="h-3 w-3 inline text-blue-500" /> {trade.entry_cost}</span>
                              <span><TrendingUp className="h-3 w-3 inline text-green-500" /> {trade.target_profit}</span>
                              <span><Lock className="h-3 w-3 inline text-red-400" /> {trade.stop_loss}</span>
                            </div>
                            {isExpanded && trade.reason && (
                              <p className="text-xs text-slate-500 mt-2 italic">{trade.reason}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic mt-2">No trade picks generated for this strategy.</p>
                    )}

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-400">Outlook:</span>
                            <span className="ml-1 text-slate-700 font-medium">{strat.market_outlook}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Max Risk:</span>
                            <span className="ml-1 text-slate-700">{strat.max_risk}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Ideal IV:</span>
                            <span className="ml-1 text-slate-700">{strat.ideal_iv}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Win Rate:</span>
                            <span className="ml-1 text-indigo-600 font-semibold">{strat.typical_win_rate}%</span>
                          </div>
                        </div>

                        {/* Additional Trades (if loaded) */}
                        {extraTrades && extraTrades.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                              <Layers className="h-3 w-3" /> Additional Recommendations
                            </p>
                            {extraTrades.map((trade, eIdx) => (
                              <div
                                key={eIdx}
                                className="bg-amber-50/50 rounded-lg p-3 border border-amber-100 mb-2"
                                data-testid={`extra-trade-${strat.id}-${eIdx}`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs font-bold bg-white">
                                    {trade.symbol}
                                  </Badge>
                                  {trade.confidence && (
                                    <Badge variant="secondary" className="text-xs capitalize">
                                      {trade.confidence}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-slate-800">{trade.action}</p>
                                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-600 flex-wrap">
                                  <span><DollarSign className="h-3 w-3 inline text-blue-500" /> {trade.entry_cost}</span>
                                  <span><TrendingUp className="h-3 w-3 inline text-green-500" /> {trade.target_profit}</span>
                                  <span><Lock className="h-3 w-3 inline text-red-400" /> {trade.stop_loss}</span>
                                </div>
                                {trade.reason && (
                                  <p className="text-xs text-slate-500 mt-2 italic">{trade.reason}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Get More Button */}
                        <Button
                          onClick={() => loadMoreTrades(strat.id)}
                          disabled={isLoadingMore}
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          data-testid={`more-btn-${strat.id}`}
                        >
                          {isLoadingMore ? (
                            <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Generating...</>
                          ) : (
                            <><ArrowRight className="h-3 w-3 mr-2" /> {extraTrades ? 'Refresh' : 'Get More'} Recommendations</>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* AskApe Expert Panel */}
          <AskApeInlinePanel />

          {/* Footer Stats */}
          <div className="text-center text-xs text-slate-400 pt-4">
            {data.stocks_scanned} stocks scanned | {data.strategy_count} strategies | Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
};

export default StrategyScanner;
